// CommandPalette.tsx — ⌘K global navigation + entity search. Pages and quick
// actions come from the local nav/action registries; entity hits (products,
// batches, transfers, prescriptions) come from the merged global search
// endpoint (GET /api/search/?q=, PIQ-BACKEND apps/search): grouped, ≤5 per
// group with honest totals, store-scoped and role-gated SERVER-SIDE. Patient
// results in particular are only ever what the server chose to serve (PH/ADM/SY,
// every patient query audited) — the palette never synthesises patient hits
// from other caches. Input is debounced ~250ms and never sent under the
// server's 3-character minimum (a hint renders instead of the 400).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from './icons';
import {
  NAV,
  STK_NAV,
  DISPENSING_NAV,
  RX_NAV,
  MST_NAV,
  COMPLIANCE_NAV,
  MASTER_NAV,
} from './chrome';
import { navPath } from '@/routes/navigation';
import { SEARCH_MIN_QUERY_LENGTH, useGlobalSearch } from '@/api/useGlobalSearch';
import type { SearchGroupKey } from '@/api/searchAdapters';
import { useStore } from '@/store/store';
import { quickActionsBus, type QuickActionId } from '@/features/quickActions/quickActionsBus';

interface Result {
  group: string;
  icon: IconName;
  label: string;
  sub?: string;
  /** Navigation target — omitted for action results. */
  to?: string;
  /** Action results run this instead of navigating. */
  run?: () => void;
}

/** Quick actions surfaced in the palette — same registry the FAB launches,
 *  filtered by capability at render time. */
const ACTIONS: { id: QuickActionId; icon: IconName; label: string; sub: string; cap: string }[] = [
  {
    id: 'adjust',
    icon: 'sliders',
    label: 'Adjust stock…',
    sub: 'Correct a batch quantity — applied immediately, audited',
    cap: 'stk.adjustment.approve',
  },
  {
    id: 'add-stock',
    icon: 'plus',
    label: 'Add stock…',
    sub: 'Receive a product without a GRN (direct import)',
    cap: 'stk.direct_import.add',
  },
  {
    id: 'new-adjustment',
    icon: 'adjustments',
    label: 'New adjustment…',
    sub: 'Draft a correction for approval',
    cap: 'adjustment.draft',
  },
];

const PAGE_GROUPS: { label: string; dashId: string; items: typeof NAV }[] = [
  { label: 'Inventory', dashId: 'dashboard', items: NAV },
  { label: 'Stocks', dashId: 'stk-dashboard', items: STK_NAV },
  // Dispensing was split out of Stocks into its own module — without this entry
  // the queue/baskets/backorders pages were unreachable from ⌘K.
  { label: 'Dispensing', dashId: 'disp-home', items: DISPENSING_NAV },
  { label: 'Prescriptions', dashId: 'rx-dashboard', items: RX_NAV },
  { label: 'Multi-Store', dashId: 'mst-dashboard', items: MST_NAV },
  { label: 'Compliance', dashId: 'compliance', items: COMPLIANCE_NAV },
  { label: 'Master Data', dashId: 'md-home', items: MASTER_NAV },
];

const PAGES: Result[] = PAGE_GROUPS.flatMap(({ label, dashId, items }) => [
  {
    group: 'Pages',
    icon: 'dashboard' as IconName,
    label: `${label} — Dashboard`,
    to: navPath(dashId),
  },
  ...items.map((n) => ({
    group: 'Pages',
    icon: n.icon,
    label: `${label} — ${n.label}`,
    to: navPath(n.id),
  })),
]);

const GROUP_ICONS: Record<SearchGroupKey, IconName> = {
  products: 'pill',
  batches: 'batches',
  transfers: 'multistore',
  prescriptions: 'fileText',
};

function score(haystack: string, query: string): number {
  const h = haystack.toLowerCase();
  const q = query.toLowerCase();
  if (!q) return 0;
  const idx = h.indexOf(q);
  if (idx === -1) return -1;
  // earlier + tighter matches first
  return 100 - idx - (h.length - q.length) * 0.05;
}

export function CommandPalette({
  open,
  onClose,
  onOpen,
}: {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}) {
  // global ⌘K / Ctrl+K toggles the palette — the listener lives here so the
  // shell only has to hold the open state.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) onClose();
        else onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, onOpen]);

  if (!open) return null;
  return <PaletteBody onClose={onClose} />;
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { can } = useStore();

  // ~250ms debounce so the server sees one request per pause, not per keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(id);
  }, [query]);

  const trimmed = query.trim();
  const term = debounced.trim();
  // never fire under the server's minimum — the hook is disabled there
  const { data: serverGroups = [], isFetching } = useGlobalSearch(term);
  const shortQuery = trimmed.length > 0 && trimmed.length < SEARCH_MIN_QUERY_LENGTH;
  // "still typing" (debounce pending) counts as searching so the empty state
  // never flashes between keystroke and request
  const searching = trimmed.length >= SEARCH_MIN_QUERY_LENGTH && (trimmed !== term || isFetching);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Local results: capability-gated quick actions + page links, fuzzy-scored.
  const localResults = useMemo<Result[]>(() => {
    const pool: { r: Result; s: number }[] = [];
    const push = (r: Result, hay: string) => {
      const s = score(hay, query);
      if (s >= 0) pool.push({ r, s });
    };
    // Actions the current role may launch — before Pages so "adjust"/"add stock"
    // surface the DOING result above the page links.
    for (const a of ACTIONS) {
      if (!can(a.cap)) continue;
      push(
        {
          group: 'Actions',
          icon: a.icon,
          label: a.label,
          sub: a.sub,
          run: () => quickActionsBus.open(a.id),
        },
        a.label,
      );
    }
    for (const p of PAGES) push(p, p.label);
    pool.sort((a, b) => b.s - a.s);
    const out: Result[] = [];
    for (const { r } of pool) {
      out.push(r);
      if (out.length >= 8) break;
    }
    return out;
  }, [query, can]);

  // Server results: rendered exactly as served, in served order. Headers get
  // the honest "n of total" count via groupCounts.
  const { serverResults, groupCounts } = useMemo(() => {
    const results: Result[] = [];
    const counts: Record<string, string> = {};
    if (term.length >= SEARCH_MIN_QUERY_LENGTH) {
      for (const g of serverGroups) {
        counts[g.label] = `${g.hits.length} of ${g.total}`;
        for (const hit of g.hits) {
          results.push({
            group: g.label,
            icon: GROUP_ICONS[g.key],
            label: hit.label,
            sub: hit.sub,
            to: hit.to,
          });
        }
      }
    }
    return { serverResults: results, groupCounts: counts };
  }, [serverGroups, term]);

  const results = useMemo(() => [...localResults, ...serverResults], [localResults, serverResults]);

  const go = (r: Result) => {
    onClose();
    if (r.run) {
      r.run();
      return;
    }
    if (r.to) navigate(r.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && results[cursor]) {
      e.preventDefault();
      go(results[cursor]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  let lastGroup = '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-backdrop)',
        zIndex: 2000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '12vh',
        animation: 'fade .12s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 640,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: '64vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-dropdown)',
          overflow: 'hidden',
          animation: 'pop .15s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: '16px 20px',
            borderBottom: '1px solid var(--divider)',
          }}
        >
          <Icon name="search" size={20} color="var(--text-secondary)" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page — or search products, batches, transfers, prescriptions…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--fs-emphasis)',
              color: 'var(--text-primary)',
            }}
          />
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              fontWeight: 700,
              color: 'var(--text-disabled)',
              border: '1px solid var(--divider)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 7px',
            }}
          >
            esc
          </span>
        </div>
        <div style={{ overflowY: 'auto', padding: 'var(--space-2)' }}>
          {results.map((r, i) => {
            const showGroup = r.group !== lastGroup;
            lastGroup = r.group;
            return (
              <div key={`${r.group}-${r.to}-${i}`}>
                {showGroup && (
                  <div
                    data-testid="palette-group-header"
                    style={{
                      padding: '10px 12px 4px',
                      fontSize: 'var(--fs-caption)',
                      fontWeight: 700,
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: 'var(--text-disabled)',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <span>{r.group}</span>
                    {groupCounts[r.group] && (
                      <span style={{ fontWeight: 600, letterSpacing: 0.2 }}>
                        {groupCounts[r.group]}
                      </span>
                    )}
                  </div>
                )}
                <div
                  onClick={() => go(r)}
                  onMouseEnter={() => setCursor(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    background: i === cursor ? 'var(--primary-lighter)' : 'transparent',
                  }}
                >
                  <Icon
                    name={r.icon}
                    size={17}
                    color={i === cursor ? 'var(--primary-main)' : 'var(--text-secondary)'}
                  />
                  <span
                    style={{
                      fontSize: 'var(--fs-emphasis)',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.label}
                  </span>
                  {r.sub && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 'var(--fs-body-sm)',
                        color: 'var(--text-disabled)',
                        fontFamily: 'var(--font-mono)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.sub}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {shortQuery && (
            <div
              style={{
                padding: '14px 12px',
                textAlign: 'center',
                color: 'var(--text-disabled)',
                fontSize: 'var(--fs-body)',
              }}
            >
              Type at least {SEARCH_MIN_QUERY_LENGTH} characters to search products, batches,
              transfers and prescriptions
            </div>
          )}
          {searching && serverResults.length === 0 && (
            <div
              style={{
                padding: '14px 12px',
                textAlign: 'center',
                color: 'var(--text-disabled)',
                fontSize: 'var(--fs-body)',
              }}
            >
              Searching…
            </div>
          )}
          {results.length === 0 && !shortQuery && !searching && (
            <div
              style={{
                padding: 28,
                textAlign: 'center',
                color: 'var(--text-disabled)',
                fontSize: 'var(--fs-body)',
              }}
            >
              No matches for “{query}”
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
